import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import {
  Eye, CheckCircle, XCircle, Globe, Cpu, FileText,
  Table2, PenTool, Shield, Languages, Sparkles
} from 'lucide-react';
import { VisionInsightsPanel, HumanValidationPanel } from '../components/vision';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const VisionIntelligence = () => {
  const { user, getAuthHeaders } = useAuth();
  const [config, setConfig] = useState(null);
  const [ocrResult, setOcrResult] = useState(null);

  useEffect(() => {
    document.title = 'ReasonDMS | Vision Intelligence';
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      const token = localStorage.getItem('reasondms_token');
      const response = await fetch(`${API_URL}/api/vision/status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setConfig(data);
      }
    } catch (err) {
      console.error('Failed to load vision config:', err);
    }
  };

  const components = [
    { key: 'advanced_ocr', label: 'Advanced OCR', icon: FileText, desc: 'Structured text extraction with confidence scores' },
    { key: 'table_extraction', label: 'Table Extraction', icon: Table2, desc: 'Detect and parse tables from documents' },
    { key: 'signature_detection', label: 'Signature Detection', icon: PenTool, desc: 'Find signatures, stamps, and seals' },
    { key: 'smart_classification', label: 'Smart Classification', icon: Shield, desc: '15+ document type categories' },
    { key: 'document_reconstruction', label: 'Document Reconstruction', icon: Sparkles, desc: 'Convert scanned to clean digital' },
    { key: 'ai_correction', label: 'AI Correction', icon: CheckCircle, desc: 'Auto-fix OCR errors with LLM' },
    { key: 'multilingual_normalization', label: 'Multilingual Normalization', icon: Languages, desc: 'Normalize business terms in English' },
    { key: 'human_validation', label: 'Human Validation', icon: Eye, desc: 'Review and correct AI results' },
  ];

  return (
    <div data-testid="vision-intelligence-page" className="space-y-6">
      {/* Header */}
      <div className="relative overflow-hidden rounded-2xl" style={{ background: 'linear-gradient(135deg, #312e81 0%, #1e1b4b 60%, #1e293b 100%)' }}>
        <div className="absolute inset-0 opacity-[0.07]">
          <div className="absolute top-8 right-16 w-56 h-56 border border-white rounded-full" />
          <div className="absolute -bottom-8 left-24 w-40 h-40 border border-white rounded-full" />
        </div>
        <div className="relative z-10 p-8">
          <div className="flex items-center gap-3 mb-2">
            <Eye className="h-8 w-8 text-indigo-300" />
            <div>
              <p className="text-xs font-medium text-indigo-300 tracking-wider uppercase">Reason DMS</p>
              <h1 className="text-2xl font-bold text-white">Vision Intelligence Engine</h1>
            </div>
          </div>
          <p className="text-slate-300 max-w-2xl text-sm mt-1">
            Advanced document digitization powered by AI. Process handwritten documents, extract tables,
            detect signatures, and reconstruct clean digital documents in 17 languages.
          </p>

          {/* Capability grid */}
          <div className="flex flex-wrap gap-2 mt-5">
            {config?.config?.supported_languages?.slice(0, 8).map(lang => (
              <Badge key={lang} className="bg-white/10 text-white/80 border-0 text-xs">
                {config?.config?.language_names?.[lang] || lang}
              </Badge>
            ))}
            {config?.config?.supported_languages?.length > 8 && (
              <Badge className="bg-white/10 text-white/80 border-0 text-xs">
                +{config.config.supported_languages.length - 8} more
              </Badge>
            )}
          </div>
        </div>
      </div>

      {/* Component Status Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
        {components.map(({ key, label, icon: Icon, desc }) => {
          const enabled = config?.config?.components?.[key] !== false;
          return (
            <Card key={key} className="hover:shadow-md transition-shadow">
              <CardContent className="pt-4 pb-3">
                <div className="flex items-start gap-3">
                  <div className={`p-2 rounded-lg ${enabled ? 'bg-indigo-50' : 'bg-slate-50'}`}>
                    <Icon className={`h-4 w-4 ${enabled ? 'text-indigo-600' : 'text-slate-400'}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-slate-800 truncate">{label}</span>
                      {enabled ? (
                        <CheckCircle className="h-3.5 w-3.5 text-emerald-500 shrink-0" />
                      ) : (
                        <XCircle className="h-3.5 w-3.5 text-slate-300 shrink-0" />
                      )}
                    </div>
                    <p className="text-xs text-slate-400 mt-0.5 line-clamp-1">{desc}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Main Processing Area */}
      <Tabs defaultValue="process" className="w-full">
        <TabsList className="grid w-full grid-cols-2 max-w-md">
          <TabsTrigger value="process" className="flex items-center gap-2">
            <Cpu className="h-4 w-4" />
            Document Processing
          </TabsTrigger>
          <TabsTrigger value="validate" className="flex items-center gap-2">
            <Eye className="h-4 w-4" />
            Validation
          </TabsTrigger>
        </TabsList>

        <TabsContent value="process" className="mt-4">
          <VisionInsightsPanel headers={getAuthHeaders()} onOcrComplete={setOcrResult} />
        </TabsContent>

        <TabsContent value="validate" className="mt-4">
          <HumanValidationPanel ocrResult={ocrResult} />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default VisionIntelligence;
